import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useNFT } from '../../context/NFTContext';
import { useWallet } from '../../context/WalletContext';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import NFTSelector from './NFTSelector';

const NFTSelectionModal = ({ onClose, onSelect, forceShow = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isConnected, address } = useWallet();
  const { tradingPortal, auth } = useSelector(state => state.auth);
  const { selectedNFT, hasNFTs, isLoading, error, loadNFTs, deselectNFT, selectNFT } = useNFT();
  const [showModal, setShowModal] = useState(false);
  const [localSelectedNFT, setLocalSelectedNFT] = useState(selectedNFT);
  const hasShownOnceRef = useRef(false);
  const wasConnectedRef = useRef(false);
  const prevAddressRef = useRef(null);

  // Rutas donde NO debe mostrarse el modal (páginas de registro/creación de NFT)
  const registrationRoutes = ['/register', '/nft/register', '/nft/select-nft-collection', '/nft/choose-character', '/nft/buy-pet', '/nft/pet-confirmation'];
  const isRegistrationRoute = registrationRoutes.some(route => location.pathname.startsWith(route));

  // Get storage key for this specific wallet
  const getModalShownKey = (walletAddress) => {
    return walletAddress ? `nftModalShownOnce_${walletAddress.toLowerCase()}` : 'nftModalShownOnce';
  };

  useEffect(() => {
    if (address) {
      const oldGlobalFlag = localStorage.getItem('nftModalShownOnce');
      if (oldGlobalFlag === 'true') {
        localStorage.removeItem('nftModalShownOnce');
      }

      const storageKey = getModalShownKey(address);
      const hasShownBefore = localStorage.getItem(storageKey);
      
      if (hasShownBefore === 'true') {
        // No deseleccionar NFT con id === 0, es un NFT válido
        hasShownOnceRef.current = true;
      } else {
        hasShownOnceRef.current = false;
        // No deseleccionar NFT automáticamente cuando no se muestra el modal
        // El usuario puede tener un NFT seleccionado válidamente
        if (selectedNFT && showModal) {
          if (storageKey) {
            localStorage.setItem(storageKey, 'true');
            hasShownOnceRef.current = true;
          }
        }
      }
      
      const walletChanged = prevAddressRef.current && 
        prevAddressRef.current.toLowerCase() !== address.toLowerCase();
      
      if (walletChanged) {
        hasShownOnceRef.current = false;
        if (prevAddressRef.current) {
          const oldStorageKey = getModalShownKey(prevAddressRef.current);
          localStorage.removeItem(oldStorageKey);
        }
        if (storageKey) {
          localStorage.removeItem(storageKey);
        }
        if (selectedNFT) {
          deselectNFT();
        }
      }
      prevAddressRef.current = address;
    }
  }, [address, deselectNFT, selectedNFT, showModal]);

  useEffect(() => {
    if (forceShow) {
      setShowModal(true);
      hasShownOnceRef.current = false;
    }
  }, [forceShow]);

  // Detectar cuando el usuario acaba de hacer login exitoso y resetear el flag
  useEffect(() => {
    const isLoggedIn = !!auth?.idToken || !!localStorage.getItem('jwt_token');
    const hasAccount = tradingPortal?.hasPortalAccount || false;
    
    if (isConnected && address && isLoggedIn && hasAccount && hasNFTs) {
      // Resetear el flag de "ya mostrado" para que aparezca el modal después del login
      hasShownOnceRef.current = false;
      const storageKey = getModalShownKey(address);
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    }
  }, [auth?.idToken, tradingPortal?.hasPortalAccount, isConnected, address, hasNFTs]);

  useEffect(() => {
    const storageKey = address ? getModalShownKey(address) : null;
    const hasShownBefore = storageKey ? localStorage.getItem(storageKey) === 'true' : false;

    if (!isConnected || !address) {
      setShowModal(false);
      wasConnectedRef.current = false;
      return;
    }

    // SOLO mostrar el modal de NFT si el usuario tiene cuenta de Trading Portal Y está logueado
    // Esto previene que el modal se muestre antes de completar el registro/login
    const hasPortalAccount = tradingPortal?.hasPortalAccount || false;
    const isLoggedIn = !!auth?.idToken || !!localStorage.getItem('jwt_token'); // Verificar también localStorage
    const isVerified = tradingPortal?.isVerified !== false; // true si es true o undefined (no requiere verificación)
    
    // Si NO tiene cuenta o NO está logueado, NO mostrar el modal de NFT
    if (!hasPortalAccount || !isLoggedIn || !isVerified) {
      setShowModal(false);
      return;
    }

    const isNewConnection = !wasConnectedRef.current && isConnected;
    if (isNewConnection) {
      hasShownOnceRef.current = false;
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    }

    wasConnectedRef.current = true;

    if (forceShow) {
      setShowModal(true);
      hasShownOnceRef.current = false;
      return;
    }

    if (hasNFTs && !isLoading) {
      // No deseleccionar NFT con id === 0, es un NFT válido (Genesis)
      // Remover esta lógica que estaba deseleccionando el primer NFT
      
      if (!selectedNFT) {
        if (hasShownBefore) {
          if (storageKey) {
            localStorage.removeItem(storageKey);
          }
          hasShownOnceRef.current = false;
        }
        
        setShowModal(true);
        hasShownOnceRef.current = true;
      } else {
        if (!hasShownOnceRef.current && !hasShownBefore) {
          setShowModal(true);
          hasShownOnceRef.current = true;
        } else if (hasShownOnceRef.current && address) {
          const modalStorageKey = getModalShownKey(address);
          localStorage.setItem(modalStorageKey, 'true');
        }
      }
    }
  }, [isConnected, hasNFTs, isLoading, forceShow, selectedNFT, address, deselectNFT, isRegistrationRoute, location.pathname, tradingPortal, auth]);

  const handleClose = () => {
    setShowModal(false);
    setLocalSelectedNFT(null);
    hasShownOnceRef.current = true;
    if (address) {
      const storageKey = getModalShownKey(address);
      localStorage.setItem(storageKey, 'true');
    }
    if (onClose) {
      onClose();
    }
  };

  const handleSelect = (nft) => {
    // Actualizar el estado local inmediatamente para que el botón responda al instante
    setLocalSelectedNFT(nft);
    // Guardar en el contexto inmediatamente para evitar problemas de sincronización
    if (nft) {
      selectNFT(nft);
    } else {
      deselectNFT();
    }
    if (address && nft) {
      const storageKey = getModalShownKey(address);
      localStorage.setItem(storageKey, 'true');
      hasShownOnceRef.current = true;
    }
    if (onSelect) {
      onSelect(nft);
    }
  };

  // Sincronizar el estado local con el contexto cuando cambie - siempre mantener sincronizado
  useEffect(() => {
    if (selectedNFT) {
      setLocalSelectedNFT(selectedNFT);
    }
  }, [selectedNFT]);

  // Inicializar localSelectedNFT inmediatamente cuando el modal se muestra y ya hay un NFT seleccionado
  useEffect(() => {
    if (showModal) {
      // Cuando el modal se muestra, asegurarse de que localSelectedNFT esté sincronizado
      if (selectedNFT) {
        setLocalSelectedNFT(selectedNFT);
      }
    }
  }, [showModal, selectedNFT]);

  // Limpiar el estado local cuando se cierre el modal
  useEffect(() => {
    if (!showModal) {
      setLocalSelectedNFT(null);
    }
  }, [showModal]);

  // Calcular si hay un NFT disponible para habilitar el botón
  const hasAvailableNFT = useMemo(() => {
    return !!(localSelectedNFT || selectedNFT);
  }, [localSelectedNFT, selectedNFT]);

  // NO mostrar el modal en rutas de registro/creación de NFT
  if (isRegistrationRoute) {
    return null;
  }

  if (!isConnected) {
    return null;
  }

  if (!showModal) {
    return null;
  }

      return (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(10, 14, 39, 0.85)', zIndex: 1050, pointerEvents: 'auto' }}
          tabIndex="-1"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleClose();
            }
          }}
        >
          <div 
            className="modal-dialog modal-lg modal-dialog-centered" 
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1051 }}
          >
            <div 
              className="modal-content" 
              style={{
                background: '#151a2e',
                border: '1px solid #1e2541',
                borderRadius: '8px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 1052
              }} 
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="modal-header" style={{ 
                background: '#151a2e',
                color: '#ffffff',
                borderBottom: '1px solid #1e2541',
                padding: '12px 16px'
              }}>
                <div>
                  <h5 className="modal-title mb-0" style={{ color: '#ffffff', fontWeight: '600', fontSize: '14px' }}>
                    {t('nft.select_nft')}
                  </h5>
                </div>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={handleClose}
                  aria-label="Close"
                  style={{ opacity: 0.8, filter: 'brightness(0) invert(1)' }}
                ></button>
              </div>
              <div className="modal-body" style={{ 
                padding: '16px', 
                maxHeight: '60vh', 
                overflowY: 'auto',
                background: '#151a2e',
                color: '#ffffff'
              }}>
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status" style={{ color: '#00e5cc' }}>
                      <span className="sr-only">{t('common.loading')}</span>
                    </div>
                    <p className="mt-2" style={{ color: '#718096', fontSize: '12px' }}>{t('nft.loading_nfts')}</p>
                  </div>
                ) : error ? (
                  <div className="alert" style={{ 
                    background: 'rgba(255, 92, 92, 0.1)', 
                    borderColor: '#ff5c5c', 
                    color: '#ff5c5c', 
                    borderRadius: '8px',
                    border: '1px solid #ff5c5c'
                  }}>
                    <h6 style={{ fontWeight: '600', marginBottom: '12px', fontSize: '13px' }}>{t('nft.error_loading')}</h6>
                    <p style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginBottom: '8px', color: '#a0aec0' }}>{error}</p>
                    <button 
                      className="btn btn-sm" 
                      onClick={() => loadNFTs()}
                      style={{ 
                        borderRadius: '6px',
                        background: 'transparent',
                        border: '1px solid #ff5c5c',
                        color: '#ff5c5c',
                        fontSize: '11px',
                        padding: '4px 12px'
                      }}
                    >
                      {t('nft.retry')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-3" style={{ 
                      background: '#0a0e27', 
                      padding: '12px 16px', 
                      borderRadius: '8px',
                      border: '1px solid #1e2541'
                    }}>
                      <p className="mb-0" style={{ fontSize: '12px', color: '#a0aec0', lineHeight: '1.5' }}>
                        {t('nft.select_nft_description')}
                      </p>
                    </div>
                    <NFTSelector onSelect={handleSelect} />
                  </>
                )}
              </div>
              <div className="modal-footer" style={{ 
                padding: '12px 16px',
                borderTop: '1px solid #1e2541',
                background: '#151a2e'
              }}>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="flex-grow-1">
                    {(localSelectedNFT || selectedNFT) ? (
                      <div className="d-flex align-items-center gap-3">
                        <div className="flex-grow-1">
                          <small className="d-block mb-1" style={{ 
                            fontSize: '11px', 
                            color: '#718096', 
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {t('nft.nft_selected')}:
                          </small>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <strong style={{ fontSize: '14px', color: '#ffffff', fontWeight: '600' }}>
                              {(localSelectedNFT || selectedNFT).name}
                            </strong>
                            <span className="badge" style={{ 
                              fontSize: '11px', 
                              background: '#1f2640', 
                              color: '#a0aec0', 
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: '1px solid #1e2541'
                            }}>
                              {t('nft.nft_id')}: {(localSelectedNFT || selectedNFT).tokenId}
                            </span>
                            <span className="badge" style={{ 
                              fontSize: '11px', 
                              background: (localSelectedNFT || selectedNFT).type === 'defily' ? 'rgba(0, 229, 204, 0.1)' : 'rgba(0, 192, 135, 0.1)',
                              color: (localSelectedNFT || selectedNFT).type === 'defily' ? '#00e5cc' : '#00c087',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: `1px solid ${(localSelectedNFT || selectedNFT).type === 'defily' ? 'rgba(0, 229, 204, 0.2)' : 'rgba(0, 192, 135, 0.2)'}`
                            }}>
                              {(localSelectedNFT || selectedNFT).type === 'defily' ? t('nft.defily') : t('nft.fund8')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center">
                        <small style={{ fontSize: '12px', color: '#718096' }}>
                          {t('nft.select_nft_to_continue')}
                        </small>
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button 
                      type="button" 
                      className="btn" 
                      onClick={handleClose}
                      style={{ 
                        borderRadius: '6px', 
                        minWidth: '100px', 
                        padding: '8px 16px',
                        background: 'transparent',
                        border: '1px solid #1e2541',
                        color: '#a0aec0',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#1f2640';
                        e.currentTarget.style.borderColor = '#1e2541';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = '#1e2541';
                      }}
                    >
                      {t('nft.cancel')}
                    </button>
                    <button 
                      type="button" 
                      className="btn" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Usar selectedNFT como fuente de verdad si localSelectedNFT no está disponible
                        const nftToUse = localSelectedNFT || selectedNFT;
                        if (nftToUse) {
                          // Si hay una selección local diferente a la del contexto, actualizar el contexto
                          if (localSelectedNFT && (!selectedNFT || selectedNFT.id !== localSelectedNFT.id)) {
                            selectNFT(localSelectedNFT);
                          }
                          // Cerrar inmediatamente - no esperar nada, el contexto ya tiene la selección
                          handleClose();
                        }
                      }}
                      disabled={!hasAvailableNFT}
                      style={{ 
                        borderRadius: '6px', 
                        minWidth: '120px',
                        padding: '8px 16px',
                        background: hasAvailableNFT ? '#00c087' : '#1f2640',
                        border: 'none',
                        color: hasAvailableNFT ? '#ffffff' : '#718096',
                        fontWeight: '600',
                        fontSize: '12px',
                        cursor: hasAvailableNFT ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease',
                        opacity: hasAvailableNFT ? 1 : 0.5
                      }}
                      onMouseEnter={(e) => {
                        if (hasAvailableNFT) {
                          e.currentTarget.style.background = '#00b079';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (hasAvailableNFT) {
                          e.currentTarget.style.background = '#00c087';
                        }
                      }}
                    >
                      {t('nft.continue')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  );
};

export default NFTSelectionModal;

